// A Send message step's fixed text as a WhatsApp (Meta) template body.
//
// Dibs submits a step's wording to Meta for the business when the step is
// set to "Automatic" WhatsApp templates. Meta needs the body with numbered
// {{1}}..{{n}} placeholders plus a sample value for each, and rejects some
// shapes outright. Pure and shared: the publish check, the builder and the
// server's template linker all convert text the same way.

/** Run-subject tokens a workflow template's placeholders may map to. Any
 *  other body_var_order entry is sent as literal text. */
export const WORKFLOW_TEMPLATE_TOKENS = [
  "customer_name",
  "listing_name",
  "booking_date",
  "booking_time",
] as const;

export type WorkflowTemplateToken = typeof WORKFLOW_TEMPLATE_TOKENS[number];

/** Example values Meta's reviewers see for each placeholder. */
export const WORKFLOW_TEMPLATE_SAMPLES: Record<WorkflowTemplateToken, string> =
  {
    customer_name: "Sarah",
    listing_name: "Trial class",
    booking_date: "12 Oct",
    booking_time: "3:00 PM",
  };

/** Meta's limit on a template body. */
export const MAX_TEMPLATE_BODY_CHARS = 1024;

export type TemplateBodyResult =
  | { ok: true; body: string; varOrder: WorkflowTemplateToken[] }
  | { ok: false; issue: string };

const TOKEN_RE = /\{\{\s*(\w+)\s*\}\}/g;

/**
 * "hey {{customer_name}}!" → { body: "hey {{1}}!", varOrder: ["customer_name"] },
 * or the reason Meta would reject it, in words an owner can act on.
 */
export function templateBodyFromText(text: string): TemplateBodyResult {
  const trimmed = text.trim();
  if (!trimmed) return { ok: false, issue: "The message is empty." };

  const unsupported = new Set<string>();
  for (const m of trimmed.matchAll(TOKEN_RE)) {
    if (!(WORKFLOW_TEMPLATE_TOKENS as readonly string[]).includes(m[1])) {
      unsupported.add(m[1]);
    }
  }
  if (unsupported.size > 0) {
    return {
      ok: false,
      issue: `A WhatsApp template can only fill in ${
        WORKFLOW_TEMPLATE_TOKENS.map((t) => `{{${t}}}`).join(", ")
      }. Remove ${[...unsupported].map((t) => `{{${t}}}`).join(", ")}.`,
    };
  }

  const varOrder: WorkflowTemplateToken[] = [];
  const body = trimmed.replace(TOKEN_RE, (_m, key: string) => {
    varOrder.push(key as WorkflowTemplateToken);
    return `{{${varOrder.length}}}`;
  });

  if (/^\{\{\d+\}\}/.test(body) || /\{\{\d+\}\}$/.test(body)) {
    return {
      ok: false,
      issue:
        "Meta doesn't accept a WhatsApp template that starts or ends with a placeholder. Add a word before or after it, e.g. \"hey {{customer_name}}!\".",
    };
  }
  if (/\{\{\d+\}\}\s*\{\{\d+\}\}/.test(body)) {
    return {
      ok: false,
      issue:
        "Meta doesn't accept two placeholders side by side in a WhatsApp template. Put some words between them.",
    };
  }
  if (body.length > MAX_TEMPLATE_BODY_CHARS) {
    return {
      ok: false,
      issue:
        `A WhatsApp template can be at most ${MAX_TEMPLATE_BODY_CHARS} characters. Shorten this message.`,
    };
  }
  return { ok: true, body, varOrder };
}
